import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import ClientTruncate from '@common/components/ClientTruncate';
import { media } from 'styled-bootstrap-grid';
import { Link } from 'shared-routing';
import { Routes } from '@common/constants';
import { DiscountPrice, FromPrice } from '@common/components/Price';
import ImgixWrapper from '@common/components/ImgixWrapper';

import { getCheapestOption } from '@utils/helpers';

const StyledPrice = styled(DiscountPrice)`
  display: inline;
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  color: ${({ theme }) => theme.defaultColors.fontPrimary};
`;

const PriceWrapper = styled.div`
  margin: 20px 0 0;
  color: ${({ theme }) => theme.defaultColors.fontSecondary};
`;

const Title = styled.h3`
  margin: 0;
  word-wrap: break-word;
  margin-bottom: auto;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  font-size: 16px;
  line-height: 20px;
  color: ${({ theme }) => theme.defaultColors.fontPrimary};

  /* workaround for missing letter-spacing support in react-truncate */

  br {
    display: none;
  }
`;

const Description = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  padding: 20px 13px;
`;

const SuggestionItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 150px;
  margin: 10px 12px;
  box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.16);
  transition: all 0.25s ease;
  color: ${({ theme }) => theme.defaultColors.fontPrimary};
  cursor: pointer;
  letter-spacing: 0.06em;
  background: #ffffff;

  ${({ isFullHeight }) =>
    isFullHeight &&
    css`
      height: calc(100% - 20px);
    `};

  img {
    width: 150px;
    height: 150px;
  }

  ${media.md`
    width: 290px;
    img {
      width: 290px;
      height: 290px;
    }
  `};

  &:hover {
    box-shadow: 0 3px 6px 4px rgba(0, 0, 0, 0.16);
  }
`;

const SolidColorBackground = styled.div`
  width: 150px;
  height: 150px;
  background: ${({ theme }) => theme.tertiaryColour};

  ${media.md`
    width: 290px;
    height: 290px;
  `};
`;

const Category = styled.p`
  font-size: 14px;
  line-height: 19px;
  text-transform: uppercase;
  letter-spacing: 1.12px;
  margin: 0 0 5px;
  color: ${({ theme }) => theme.defaultColors.fontSecondary};

  /* workaround for missing letter-spacing support in react-truncate */

  br {
    display: none;
  }
`;

const routesMap = {
  treatment: Routes.SPA_TREATMENT_DETAIL,
  'spa-treatment': Routes.SPA_TREATMENT_DETAIL,
  'spa-day': Routes.SPA_DAY_DETAIL,
  event: Routes.EVENT_DETAIL,
  voucher: Routes.VOUCHER_DETAIL,
  product: Routes.PRODUCT_DETAIL,
};

/**
 * @deprecated New version in AddedToBasket/Fulfilable
 */
const SuggestionItem = ({
  image = null,
  name,
  id = null,
  categories = null,
  isFullHeight = false,
  onClick = () => {},
  options = null,
  leadingCategoryId = null,
  discount,
  ...item
}) => {
  const { price } = options && options.length > 0 ? getCheapestOption(options) : item;
  const hasMultiplePrices =
    (options && options.length > 0) ||
    item.type === 'treatment' ||
    /* API could send both values */ item.type === 'spa-treatment' ||
    /* API could send both values */ item.type === 'spa-day';
  const firstCategory = categories && categories.length > 0 ? categories[0] : null;
  const leadingCategory = leadingCategoryId
    ? categories.find((cat) => cat.id === leadingCategoryId)
    : null;
  const displayedCategory = leadingCategory || firstCategory;

  return (
    <Link route={routesMap[item.type]} params={{ id }} shallow={false}>
      <SuggestionItemWrapper isFullHeight={isFullHeight} onClick={onClick}>
        {image ? (
          <ImgixWrapper src={image.url} width={290} height={290} alt={name} />
        ) : (
          <SolidColorBackground />
        )}
        <Description>
          {displayedCategory && (
            <Category>
              <ClientTruncate lines={1}>{displayedCategory.name}</ClientTruncate>
            </Category>
          )}
          <Title>
            <ClientTruncate lines={2}>{typeof name === 'string' && name}</ClientTruncate>
          </Title>
          <PriceWrapper>
            {hasMultiplePrices ? (
              <FromPrice price={price} discount={discount} />
            ) : (
              <StyledPrice price={price} discount={discount} />
            )}
          </PriceWrapper>
        </Description>
      </SuggestionItemWrapper>
    </Link>
  );
};

SuggestionItem.propTypes = {
  image: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      url: PropTypes.string,
    }),
  ]),
  name: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  id: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]),
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })
  ),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      price: PropTypes.object,
    })
  ),
  isFullHeight: PropTypes.bool,
  onClick: PropTypes.func,
  leadingCategoryId: PropTypes.number,
};

export default SuggestionItem;
